import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { readSheet } from "read-excel-file/browser";
import type { SheetData } from "read-excel-file/browser";
import {Modal, Text} from '@sede-enterprise/shell-ds-react-framework'
import { BidStatus, IBid, IBidPanelRowDetails } from "../../services";
import uuid from "react-uuid";
import moment from "moment";

interface ISpreadsheetDetails {
  Interconnector: string;
  Corridor: string;
  PeriodEndDate: string;
}

interface IExcelDropzoneProps {
  corridor: string;
  interconnector: string;
  periodEndDate: Date;
  currentBids: IBid[];
  onSuccessfulValidation: (newBids: IBid[]) => void;
}

interface IITABidUpload {
  product: string;
  volume: number;
  price: number;
}

interface IProductVolume {
  product: string;
  volume: number;
  price: number;
  [product: string]: any;
}

const isSheetNotFoundError = (error: unknown) =>
  error instanceof Error && error.name === "SheetNotFoundError";

const readWorksheet = async (arrayBuffer: ArrayBuffer, sheetName: string): Promise<SheetData | undefined> => {
  try {
    return await readSheet(arrayBuffer.slice(0), sheetName);
  } catch (error) {
    if (isSheetNotFoundError(error)) {
      return undefined;
    }
    throw error;
  }
};

const getCellValue = (value: unknown, defaultValue: any) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value === 'object' && value !== null && 'result' in value) {
    return value.result;
  }
  return value;
};

const worksheetToJson = <T extends Record<string, any>>(worksheet: SheetData, defaultValue: any = undefined): T[] => {
  const rows: T[] = [];
  const headers = worksheet[0] ?? [];

  worksheet.slice(1).forEach((row) => {
    const rowData: Record<string, any> = {};
    headers.forEach((headerCell, colIndex) => {
      const header = String(headerCell ?? '');
      if (header) {
        rowData[header] = getCellValue(row[colIndex], defaultValue);
      }
    });
    rows.push(rowData as T);
  });
  
  return rows;
};

export const ExcelDropzone = ({
  corridor,
  interconnector,
  periodEndDate,
  currentBids,
  onSuccessfulValidation,
}: IExcelDropzoneProps) => {
  const [errors, setErrors] = useState<string[]>([]);

  const onDrop = useCallback((acceptedFiles: any) => {
    acceptedFiles.forEach((file: any) => {
      const reader = new FileReader();
      reader.onabort = () => console.log("File reading was aborted");
      reader.onerror = () => console.log("File reading has failed");
      reader.onload = () => {
        processData(reader);
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const extractDetails = (wsDetails: SheetData | undefined, wsData: SheetData | undefined): any => {
    try {
      if (!wsDetails || !wsData) {
        return {
          corridorExcel: "",
          interconnectorExcel: "",
          convertedDateExcel: moment.invalid(),
          data: []
        };
      }
      
      const details = worksheetToJson<ISpreadsheetDetails>(wsDetails);
      const detailsRow = details[0];
      const interconnectorExcel = detailsRow["Interconnector"];
      const corridorExcel = String(detailsRow["Corridor"]).replace("_", "-");
      const dateStringExcel = detailsRow["PeriodEndDate"];
      const convertedDateExcel = moment(dateStringExcel, "DD/MM/YYYY");

      const data = worksheetToJson(wsData, 0);
      return {
        corridorExcel,
        interconnectorExcel,
        convertedDateExcel,
        data
      };
    } catch (error) {
      console.error("Error extracting details:", error);
      return {
        corridorExcel: "",
        interconnectorExcel: "",
        convertedDateExcel: moment.invalid(),
        data: []
      };
    }
  }
  
  const processData = async (reader: FileReader) => {
    let wsDetails: SheetData | undefined;
    let wsData: SheetData | undefined;
    try {
      if (!reader.result) {
        throw new Error("No file data available to process.");
      }
      const arrayBuffer = reader.result as ArrayBuffer;
      wsDetails = await readWorksheet(arrayBuffer, "Details");
      wsData = await readWorksheet(arrayBuffer, "Data");
    } catch (error) {
      console.error("Error loading Excel workbook:", error);
      setErrors((prevErrors) => [
        ...prevErrors,
        "Failed to read the uploaded Excel file. Please check the file and try again.",
      ]);
      return;
    }

    // get the two pieces of info about which auction it is
    const {
      corridorExcel,
      interconnectorExcel,
      convertedDateExcel,
      data
    } = extractDetails(wsDetails, wsData);
    
    let criticalError = false;
    let errorsFound: string[] = [];

    //validate auction details are correct
    if(!wsDetails){
      errorsFound.push("'Details' sheet is missing");
    }
    if(!wsData){
      errorsFound.push("'Data' sheet is missing");
    }
    if (corridor !== corridorExcel) {
      errorsFound.push(corridorExcel + " corridor needs to match " + corridor);
    }
    if (interconnector !== interconnectorExcel) {
      errorsFound.push(
        interconnectorExcel + " interconnector needs to match " + interconnector
      );
    }

    const periodEndDateOnly = moment(periodEndDate).clone().startOf("day");
    if (convertedDateExcel.format("YYYY-MM-DD") === "Invalid date") {
      errorsFound.push(
        "Ensure that the delivery date is entered as text in the format DD/MM/YYYY and formatted as text"
      );
    } else if (!periodEndDateOnly.isSame(convertedDateExcel)) {
      errorsFound.push(
        convertedDateExcel.format("DD/MM/YYYY") +
          " date does not match " +
          periodEndDateOnly.format("DD/MM/YYYY")
      );
    }

    if(errorsFound.length === 0)
    {
      //validate all column names and numbering
      const singleDataObject: any = data[0];
      //get all properties from the first row of the array
      const numberOfColumns = Object.getOwnPropertyNames(singleDataObject).length;
      //get number of product rows
      const numberOfRows = data.length;
      //-1 to get the column number because there's the Product column
      const numberOfBids = (numberOfColumns - 1) / 2;

      //check all the expected column names exist
      for (let i = 1; i <= numberOfBids; i++) {
        const propertyNamePrice = "Price" + i;
        if (!singleDataObject.hasOwnProperty(propertyNamePrice)) {
          errorsFound.push(propertyNamePrice + " missing");
        }
        const propertyNameVolume = "Vol" + i;
        if (!singleDataObject.hasOwnProperty(propertyNameVolume)) {
          errorsFound.push(propertyNameVolume + " missing");
        }
      }

      // Stop processing if column validation failed - prevents crashes when accessing malformed data
      if (errorsFound.length > 0) {
        setErrors(errorsFound);
        return;
      }

      //transform data into individual bids
      let newBids = [];
      for (let bid = 1; bid <= numberOfBids; bid++) {
        let newBid = [] as Array<IITABidUpload>;
        for (let row = 0; row < numberOfRows; row++) {
          const record: any = data[row];
          const newRecord = {
            product: record["Product"],
            volume: record["Vol" + bid],
            price: record["Price" + bid],
          };
          newBid.push(newRecord);
        }
        newBids.push(newBid);
      }

      //maximum bids
      if (newBids.length > 20) {
        errorsFound.push(`Only maximum 20 bids are allowed`);
        criticalError = true;
      }

      //validate all volumes as numbers
      const volumeNumberIssues = newBids.filter((x) =>
        x.some((y) => isNaN(y.volume))
      ).length;
      if (volumeNumberIssues > 0) {
        errorsFound.push(
          `${volumeNumberIssues} bid(s) with volumes with non-number characters, which aren't allowed`
        );
        criticalError = true;
      }

      //validate no negative volumes
      const volumeNegativeIssues = newBids.filter((x) =>
        x.some((y) => y.volume < 0)
      ).length;
      if (volumeNegativeIssues > 0) {
        errorsFound.push(
          `${volumeNegativeIssues} bid(s) with negative volumes, which aren't allowed`
        );
        criticalError = true;
      }

      //validate all volumes as numbers
      const priceNumberIssues = newBids.filter((x) =>
        x.some((y) => isNaN(y.price))
      ).length;
      if (priceNumberIssues > 0) {
        errorsFound.push(
          `${priceNumberIssues} bid(s) with prices with non-number characters, which aren't allowed`
        );
        criticalError = true;
      }

      //validate no negative prices
      const priceNegativeIssues = newBids.filter((x) =>
        x.some((y) => y.price < 0)
      ).length;
      if (priceNegativeIssues > 0) {
        errorsFound.push(
          `${priceNegativeIssues} bid(s) with negative prices, which aren't allowed`
        );
        criticalError = true;
      }

      //need to extract products and maximum quantities from the first bid, which gets them from the server
      const bidOne: IBid = currentBids[0];
      const rowDetails = bidOne.bidPanelRowDetails;
      const productHours = rowDetails!.map((x) => x.productHour);

      //validate a match in the number of product hours
      const numberProducts = productHours?.length;
      if (numberOfRows !== numberProducts) {
        errorsFound.push(
          "Wrong number of products: " + numberOfRows + " vs " + numberProducts
        );
        criticalError = true;
      }

      //check whether any bids have all zeros in them
      const bidVolumesAllZero = newBids.filter(
        (x) =>
          x.reduce((acc: number, record: IITABidUpload) => {
            acc = (acc || 0) + record.volume;
            return acc;
          }, 0) === 0
      );
      const numberOfAllZeroBids = bidVolumesAllZero.length;
      if (numberOfAllZeroBids > 0) {
        errorsFound.push(
          `Volumes for ${
            numberOfAllZeroBids === 1
              ? "1 bid is"
              : numberOfAllZeroBids + " bids are"
          } composed of all zeros, which is not allowed`
        );
      }

      if (!criticalError) {
        //validate all volumes as 0dp max
        const volumeDpIssues = newBids.filter((x) =>
          x.some((y) => !Number.isInteger(y.volume))
        ).length;
        if (volumeDpIssues > 0) {
          errorsFound.push(
            `${volumeDpIssues} bid(s) with volumes with decimal places, which aren't allowed`
          );
        }

        //validate all prices as numbers with 2dp max
        const regex = new RegExp("^[0-9]*(.[0-9]{1,2})?$");
        const priceDpIssues = newBids.filter((x) =>
          x.some((y) => !regex.test(y.price.toString()))
        );

        if (priceDpIssues.length > 0) {
          errorsFound.push(
            `${priceDpIssues.length} bid(s) with prices with more than 2 decimal places, which aren't allowed`
          );
        }

        ///validate against first bid since all the bids have the same original product structure
        for (let record = 0; record < numberProducts; record++) {
          if (newBids[0][record].product !== productHours[record]) {
            errorsFound.push(
              "Product mismatch between " +
                newBids[0][record].product +
                " and " +
                productHours[record]
            );
          }
        }

        const capacities = rowDetails!.reduce((acc, x) => {
          return {
            [x.productHour]: x.offeredCapacity,
            ...acc,
          };
        }, {});
        const capacityList = Object.entries(capacities).map((x) => ({
          product: x[0],
          volume: x[1],
        }));

        const volumes = newBids.flat().reduce((acc: IProductVolume, i) => {
          acc[i.product] = (acc[i.product] || 0) + i.volume;
          return acc;
        }, {} as IProductVolume);
        const volumeList = Object.entries(volumes).map((x) => ({
          product: x[0],
          volume: x[1],
        }));

        //validate that totals for each product are not above the offered quantities
        volumeList.forEach((x) => {
          const cap = capacityList.find(
            (c) => c.product === x.product
          ) as IProductVolume;
          if (cap && cap.volume < x.volume) {
            errorsFound.push(
              `Volume for ${x.product} is above the offered quantity of ${cap?.volume}`
            );
          }
        });
      }
      // create the new bids if there are no errors
      let allNewBids: IBid[] = [];
      // get some existing static data from the first old bid
      const firstBid = currentBids[0];
      const auctionIdentifiction = firstBid.auctionIdentification;
      const rowD = firstBid.bidPanelRowDetails!;
      const corridorCodeX = rowD[0].corridorCode;
      const bidGuid = uuid();
      newBids.forEach((x, index) => {
        // create single bid and add the bid panel row details with each product hour
        let rowDetails: IBidPanelRowDetails[] = [];
        x.forEach((y, index) => {
          const rowDetail: IBidPanelRowDetails = {
            productHour: y.product,
            quantity: y.volume,
            price: y.price,
            bidGuid: bidGuid,
            corridorCode: corridorCodeX ? corridorCodeX : "",
            position: index + 1,
            offeredCapacity:
              firstBid.bidPanelRowDetails?.find(
                (b) => b.productHour === y.product
              )?.offeredCapacity ?? 0,
          };
          rowDetails.push(rowDetail);
        });
        const newBid: IBid = {
          auctionIdentification: auctionIdentifiction,
          corridorCode: corridorCodeX,
          id: index + 1,
          bidGuid: bidGuid,
          bidPanelRowDetails: rowDetails,
          bidItems: [],
          status: currentBids[index]
            ? currentBids[index].status === BidStatus.Accepted ||
              currentBids[index].status === BidStatus.Amended
              ? BidStatus.Amended
              : BidStatus.Draft
            : BidStatus.Draft,
          isDirty: true,
          exchangeId: firstBid.exchangeId,
        };

        allNewBids.push(newBid);
      });

      // Only call onSuccessfulValidation if there are no errors
      if (errorsFound.length === 0) {
        onSuccessfulValidation(allNewBids);
      }
    }

    setErrors(errorsFound);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
    maxFiles: 1,
  });

  const close = () => {
    setErrors([]);
    const element = document.getElementById('shell-modal-container-portal');
    if (element) {
        element.style.display = "none";
    }
  };
  useEffect(()=>{
    if(errors){
      const element = document.getElementById('shell-modal-container-portal');
      if(element){
        element.style.display="block";
      }
    }
  },[errors])

  return (
    <React.Fragment>
      <Container {...getRootProps()}>
        <input {...getInputProps()} />
        <StyledDiv  >Import spreadsheet</StyledDiv>
      </Container>
      <StyledModal
        title="Spreadsheet Errors"
        onClose={close}
        open={errors.length>0}
      >
        {errors.map((x) => (
          <Text size="medium" > {x} </Text>
        ))}
      </StyledModal>
    </React.Fragment>
  );
};

const StyledDiv = styled.div`
  margin: 0 0 -10px 0 !important;
  color: black;
  padding: 3px;
`;

const StyledModal = styled(Modal)`
  box-shadow:none;
  border:none;
  outline:none;
  color: black;
  z-index:10000;
  background:transparent;
  .shell-modal-container-body p{
    margin-bottom:10px;
  }
`;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3px;
  border-width: 2px;
  border-radius: 2px;
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
  margin-right:10px;
`;

